import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Создание и редактирование компании. До этого раздел был только на чтение:
// компании появлялись лишь через seed.
import { useEffect, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Form, Input, Modal } from 'antd';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  createCompany,
  updateCompany,
  type CompanyDetail,
  type CompanyInput,
} from '../api/companies.client.js';

interface CompanyFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Передана — режим редактирования. */
  company?: CompanyDetail | null;
}

export function CompanyFormModal({ open, onClose, company }: CompanyFormModalProps): ReactElement {
  useI18nSubscription();
  const [form] = Form.useForm<CompanyInput>();
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const isEdit = Boolean(company);

  const invalidate = (): void => {
    // Префикс покрывает и список, и открытую карточку (detail-ключ — это
    // ['companies', workspaceId, id], отдельный singular-ключ не существует).
    void queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] });
  };

  const mutation = useMutation({
    mutationFn: (input: CompanyInput) =>
      company ? updateCompany(company.id, input) : createCompany(input),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Зависим только от reset: сам объект мутации меняется на каждый рендер и
  // сбрасывал бы уже введённые значения.
  const resetMutation = mutation.reset;
  useEffect(() => {
    if (!open) return;
    resetMutation();
    form.setFieldsValue({
      name: company?.name ?? '',
      industry: company?.industry ?? '',
      domain: company?.domain ?? '',
      website: company?.website ?? '',
      phone: company?.phone ?? '',
      description: company?.description ?? '',
    });
  }, [open, company, form, resetMutation]);

  return (
    <Modal
      title={
        isEdit
          ? i18n.t(
              'crm.features.features.companies.components.companyformmodal.редактировать_компанию',
            )
          : i18n.t('crm.features.features.companies.components.companyformmodal.новая_компания')
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.companies.components.companyformmodal.сохранить')
          : i18n.t('crm.features.features.companies.components.companyformmodal.создать')
      }
      cancelText={i18n.t('crm.features.features.companies.components.companyformmodal.отмена')}
      confirmLoading={mutation.isPending}
      width={520}
      destroyOnHidden
    >
      {mutation.isError ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={i18n.t(
            'crm.features.features.companies.components.companyformmodal.не_удалось_сохранить_компанию',
          )}
          description={(mutation.error as Error).message}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        disabled={mutation.isPending}
        onFinish={(values) =>
          mutation.mutate(
            isEdit && company ? { ...values, expectedUpdatedAt: company.updatedAt } : values,
          )
        }
      >
        <Form.Item
          name="name"
          label={i18n.t('crm.features.features.companies.components.companyformmodal.название')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: i18n.t(
                'crm.features.features.companies.components.companyformmodal.введите_название_компании',
              ),
            },
          ]}
        >
          <Input
            placeholder={i18n.t(
              'crm.features.features.companies.components.companyformmodal.ооо_ромашка',
            )}
            maxLength={200}
            autoFocus
          />
        </Form.Item>

        <div className="flex gap-3">
          <Form.Item
            name="industry"
            label={i18n.t('crm.features.features.companies.components.companyformmodal.отрасль')}
            className="flex-1"
          >
            <Input
              placeholder={i18n.t(
                'crm.features.features.companies.components.companyformmodal.услуги_ритейл',
              )}
              maxLength={100}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            label={i18n.t('crm.features.features.companies.components.companyformmodal.телефон')}
            className="flex-1"
          >
            <Input placeholder="+7…" maxLength={30} />
          </Form.Item>
        </div>

        <div className="flex gap-3">
          <Form.Item
            name="domain"
            label={i18n.t('crm.features.features.companies.components.companyformmodal.домен')}
            className="flex-1"
          >
            <Input placeholder="example.com" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="website"
            label={i18n.t('crm.features.features.companies.components.companyformmodal.сайт')}
            className="flex-1"
          >
            <Input placeholder="https://example.com" maxLength={500} />
          </Form.Item>
        </div>

        <Form.Item
          name="description"
          label={i18n.t('crm.features.features.companies.components.companyformmodal.описание')}
        >
          <Input.TextArea
            rows={3}
            maxLength={10_000}
            placeholder={i18n.t(
              'crm.features.features.companies.components.companyformmodal.чем_занимается_компания',
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
